import { useState, useEffect, useRef } from "react";
import { resolveScreenshotUrl, refreshScreenshotUrl } from '../../../../../../utils/screenshotUtils';
import ScreenshotPlaceholder from '../../../shared/ScreenshotPlaceholder';
import styles from '../../../../../../styles/automationStudio/automationStudio.module.scss';

/**
 * Renders a step-results thumbnail; shows shared loading / empty states when needed.
 */
export default function ResolvedScreenshot({ rawPath, alt = "Step screenshot", className }) {
    const [resolvedUrl, setResolvedUrl] = useState(null);
    const [imgError, setImgError] = useState(false);
    const [isResolving, setIsResolving] = useState(false);
    const retriedRef = useRef(false);

    useEffect(() => {
        setImgError(false);
        retriedRef.current = false;
        if (!rawPath || typeof rawPath !== 'string' || !rawPath.trim()) {
            setResolvedUrl(null);
            setIsResolving(false);
            return undefined;
        }
        let cancelled = false;
        setIsResolving(true);
        resolveScreenshotUrl(rawPath).then(({ url }) => {
            if (cancelled) return;
            setResolvedUrl(url || null);
            setIsResolving(false);
        });
        return () => { cancelled = true; };
    }, [rawPath]);

    if (!rawPath || typeof rawPath !== 'string' || !rawPath.trim()) {
        return <ScreenshotPlaceholder variant="empty" size="thumbnail" />;
    }

    if (isResolving) {
        return <ScreenshotPlaceholder variant="loading" size="thumbnail" />;
    }

    if (imgError || !resolvedUrl) {
        return (
            <ScreenshotPlaceholder
                variant={imgError ? 'unavailable' : 'empty'}
                size="thumbnail"
            />
        );
    }

    return (
        <img
            src={resolvedUrl}
            alt={alt}
            className={className}
            onError={async () => {
                if (retriedRef.current) {
                    setImgError(true);
                    return;
                }
                retriedRef.current = true;
                const { url } = await refreshScreenshotUrl(rawPath);
                if (url) {
                    setResolvedUrl(url);
                } else {
                    setImgError(true);
                }
            }}
        />
    );
}
